import type { ApiClient } from '@carbonwebmail/api-client'
import type { SegmentDto, SegmentListDto, SegmentViewDto } from '@carbonwebmail/contracts'
import type { SegmentDefinition, SegmentFilterRule, SegmentSort } from '@carbonwebmail/domain'
import { csvFile, fileNamed, segmentMatches } from '@carbonwebmail/domain'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import type { AsyncState } from '../../ui/Async.js'
import { downloadFile } from '../../ui/downloadFile.js'
import { useAction } from '../../ui/useAction.js'
import { useColumnLabels } from '../../ui/useColumnLabels.js'
import { useDeclaredColumns } from '../../ui/useDeclaredColumns.js'
import { useSelection } from '../../ui/useSelection.js'
import { matches, ordered, segmentBoardColumns, valueIn } from './segmentColumns.js'
import { useSegmentList } from './useSegmentList.js'

/**
 * The segments board: what is listed, what is ticked, and what can be done.
 *
 * A segment list is a table of rows like any other, which is the whole point of
 * this screen — the owner asked for it in those words. So it ticks rows through
 * the same `useSelection` the contacts board uses, reports through the same
 * `useAction`, and shares its column and sort pickers.
 *
 * **Searching and ordering happen here, not on the server.** Every other list
 * in this product pages and asks the server, and this one deliberately does
 * not: a tenant has tens of segments, not tens of thousands, and the whole list
 * already arrives in one response. Paging it would mean a second answer to
 * "which segments are there" for no gain a person would notice. If a tenant
 * ever has enough segments for that to be wrong, this is the place that has to
 * change, and `client.segments()` is the call that would grow a query.
 *
 * **The chosen view lives in the url**, so a refresh, a back button and a
 * pasted link all land on the same arrangement.
 *
 * **Deleting is refused while a campaign points at a segment**, and the refusal
 * is the message rather than something swallowed: a sent campaign's audience is
 * part of the record of what happened.
 */

export type SegmentsState = AsyncState<SegmentListDto>

/** The url's name for the open view. Read and set only here: the tab strip asks
 * this hook to change it rather than building a link of its own. */
const VIEW = 'view'

/**
 * Something somebody changed this visit, or nothing.
 *
 * Wrapped rather than held bare, because a sort has two absences that mean
 * opposite things: "I have not touched this, use the view's" and "I chose the
 * board's own order". Held as a bare `undefined` those collapse into one, and
 * clearing the sort on a view that has one then does nothing at all — which is
 * the state the contacts board is still in.
 */
interface Touched<T> {
  readonly value: T
}

/** One filter rule with an identity of its own, so a row can be edited in place. */
export interface KeyedSegmentRule {
  readonly key: string
  readonly rule: SegmentFilterRule
}

export function useSegments({
  client,
  views,
}: {
  readonly client: ApiClient
  /** The saved views, so the open one decides what the board opens as. */
  readonly views: readonly SegmentViewDto[]
}) {
  const { state, reload } = useSegmentList({ client })
  /** The columns this organisation declared on its segments, and what edits one. */
  const declared = useDeclaredColumns({ client, subject: 'segments' })
  /**
   * What it calls the four the board came with.
   *
   * The same hook the contacts board uses. Renaming one is stored rather than
   * refused since 2026-09-19 — the two boards offer the same things, which is
   * what the owner asked for.
   */
  const named = useColumnLabels({ client, subject: 'segments' })

  const [params, setParams] = useSearchParams()
  const viewId = params.get(VIEW) ?? undefined
  const view = views.find((one) => one.viewId === viewId)

  const [search, setSearch] = useState<Touched<string> | undefined>(undefined)
  const [sort, setSort] = useState<Touched<SegmentSort | undefined> | undefined>(undefined)
  const [hidden, setHidden] = useState<Touched<ReadonlySet<string>> | undefined>(undefined)
  const [rules, setRules] = useState<Touched<readonly KeyedSegmentRule[]> | undefined>(undefined)
  /**
   * A key per rule, because rows are edited in place and React has to tell one
   * from another. Keyed by position, removing a middle row shifts every key
   * after it and the value somebody typed jumps to the row above — which is
   * what `noArrayIndexKey` is for. The keys never leave this hook.
   */
  const minted = useRef(0)
  const keyed = useCallback((rule: SegmentFilterRule): KeyedSegmentRule => {
    minted.current += 1
    return { key: `r${minted.current}`, rule }
  }, [])

  /**
   * Opening a tab shows that tab's arrangement, so whatever was changed in the
   * last one is let go. A search that survived the switch would narrow a list
   * somebody had just asked to see whole.
   */
  const chooseView = useCallback(
    (next: string | undefined) => {
      setSearch(undefined)
      setSort(undefined)
      setHidden(undefined)
      setRules(undefined)
      setParams(next === undefined ? {} : { [VIEW]: next })
    },
    [setParams],
  )

  // What is in force: this visit's change where there was one, else the view's.
  const searching = search?.value ?? view?.search ?? ''
  const ordering = sort === undefined ? (view?.sort ?? undefined) : sort.value
  const hiding = hidden?.value ?? new Set(view?.hidden ?? [])
  /*
   * A view's rules arrive without keys, so they are given them once here —
   * memoised on the view, or every render would mint a new set and React would
   * rebuild each row as somebody typed into it.
   */
  const fromView = useMemo(
    () => (view?.filter?.children ?? []).map((rule, at) => ({ key: `v${at}`, rule })),
    [view],
  )
  const filtering = rules?.value ?? fromView
  const asked = useMemo(() => filtering.map((one) => one.rule), [filtering])

  const selection = useSelection()

  /**
   * Reloading drops the ticks, because they belonged to the rows being
   * replaced. The same rule the contacts board follows, and the reason a
   * selection cannot outlive what it was a selection of.
   */
  const refresh = useCallback(async () => {
    selection.clear()
    await reload()
  }, [reload, selection.clear])

  const { notice, problem, busy, run } = useAction(refresh)

  /**
   * What is on screen, which is what a selection, a count and an export are all
   * about.
   */
  const shown = useMemo(() => {
    const all = state.status === 'ready' ? state.value.segments : []
    /*
     * "Now" is taken once per render rather than per row, so every "changed
     * within 7 days" rule in one pass answers against the same instant. Per row
     * it could straddle a midnight and put two segments on opposite sides of a
     * boundary in one list.
     */
    const now = new Date()

    return ordered(
      all.filter(
        (one) =>
          matches(one, searching) &&
          segmentMatches(
            {
              name: one.name,
              // The wire shape differs from the domain's only in how optionality
              // is spelt, which the contract has already checked — the same cast
              // every route makes at this boundary.
              definition: one.definition as SegmentDefinition,
              createdAt: new Date(one.createdAt),
              updatedAt: new Date(one.updatedAt),
            },
            { children: asked },
            now,
          ),
      ),
      ordering,
    )
  }, [state, searching, ordering, asked])

  /** Every column the board could draw: the four built in, then the declared. */
  const all = useMemo(
    () => segmentBoardColumns(declared.fields, named.labels),
    [declared.fields, named.labels],
  )

  /** The columns still drawn, in order, which is also what an export carries. */
  const columns = useMemo(() => all.filter((column) => !hiding.has(column.id)), [all, hiding])

  /**
   * Deleting the ticked segments.
   *
   * One request each rather than one carrying the list, because **each segment
   * answers for itself**: a campaign may have been sent to one of them, and
   * that one is kept while the others go. A single request would have to
   * return a verdict per id anyway, and the number here is tens — this is a
   * tick list on a screen, not a forty-thousand-row import.
   *
   * What comes back names how many were kept and why. "3 deleted" while a
   * fourth silently survived is the kind of answer that makes somebody press
   * it again.
   */
  const removeMany = useCallback(
    (segmentIds: readonly string[]) =>
      run(async () => {
        const kept: string[] = []
        let deleted = 0

        for (const segmentId of segmentIds) {
          if ((await client.deleteSegment(segmentId)) === 'in-use') kept.push(segmentId)
          else deleted += 1
        }

        const gone = `${deleted === 1 ? '1 segment' : `${deleted} segments`} deleted.`
        return kept.length === 0
          ? gone
          : `${gone} ${kept.length === 1 ? 'One was' : `${kept.length} were`} kept: a campaign was sent to it, and deleting it would erase who that message went to.`
      }, 'We could not delete those segments. Try again in a moment.'),
    [client, run],
  )

  /**
   * Renaming in place, from the row's own menu.
   *
   * The definition travels unchanged because saving replaces a segment whole —
   * so a rename that sent only the name would empty the rules, and the audience
   * of every campaign pointed at it with them.
   */
  const edit = useCallback(
    (segment: SegmentDto, name: string, custom: Readonly<Record<string, string>>) =>
      run(async () => {
        const result = await client.saveSegment(
          // The rules travel unchanged: saving replaces a segment whole, so a
          // save that sent only the name would empty them — and the audience of
          // every campaign pointed at it with them.
          { name, definition: segment.definition, custom },
          segment.segmentId,
        )
        if (result.status === 'name-taken') return `A segment is already called "${name}".`
        return `"${name}" saved.`
      }, 'We could not save that segment. Try again in a moment.'),
    [client, run],
  )

  /**
   * Setting one declared column across the ticked segments.
   *
   * **One request each, carrying that segment's own name and definition.**
   * Saving replaces a segment whole, so a save that sent only the new value
   * would empty the rules — and with them the audience of every campaign
   * pointed at it. Each row's own are read from what is already on screen and
   * sent back unchanged, which is the same care `edit` takes for a rename.
   *
   * **Clearing removes the key rather than storing `""`**, so "has no owner"
   * stays one answer rather than two that look alike on the board and differ in
   * a filter.
   *
   * A tick list only, never "everything matching". Rewriting rows the server
   * resolved after the board was drawn is not something to put behind one press.
   */
  const editMany = useCallback(
    (segmentIds: readonly string[], key: string, value: string, label: string) =>
      run(async () => {
        const rows = state.status === 'ready' ? state.value.segments : []
        const chosen = rows.filter((one) => segmentIds.includes(one.segmentId))

        for (const segment of chosen) {
          const custom = { ...segment.custom }
          if (value === '') delete custom[key]
          else custom[key] = value

          await client.saveSegment(
            { name: segment.name, definition: segment.definition, custom },
            segment.segmentId,
          )
        }

        const many = chosen.length === 1 ? '1 segment' : `${chosen.length} segments`
        return value === '' ? `${label} cleared on ${many}.` : `${label} set on ${many}.`
      }, 'We could not change those segments. Try again in a moment.'),
    [client, run, state],
  )

  /**
   * The board itself as a file: the rows the search left, in the order they are
   * on screen, with the columns that are showing.
   *
   * What somebody is looking at is what they asked for a file of. Handing them
   * the unfiltered list instead would answer a question they did not ask — and
   * this needs no request at all, because the whole list is already here.
   */
  const exportShown = useCallback(() => {
    downloadFile(
      'segments.csv',
      csvFile(
        columns.map((column) => column.label),
        shown.map((segment) => columns.map((column) => valueIn(segment, column))),
      ),
    )
  }, [columns, shown])

  /** The members as a file, the same export the "who it reaches" screen offers. */
  const exportMembers = useCallback(
    (segment: SegmentDto) =>
      run(async () => {
        downloadFile(
          `${fileNamed(segment.name, 'segment')}.csv`,
          await client.exportSegment(segment.segmentId),
        )
        return `"${segment.name}" exported.`
      }, 'We could not build that file. Try again in a moment.'),
    [client, run],
  )

  return {
    state,
    shown,
    columns,
    allColumns: all,
    fields: declared.fields,
    /**
     * Renames one of the four the board came with. Takes its id, because that
     * is what the label table is keyed on — a declared column is renamed by
     * `editColumn`, which also carries the kind and the choices it must not
     * lose.
     */
    renameColumn: named.rename,
    editColumn: declared.edit,
    removeColumn: declared.remove,
    reloadColumns: declared.reload,
    view,
    viewId,
    chooseView,
    search: searching,
    setSearch: useCallback((value: string) => setSearch({ value }), []),
    sort: ordering,
    setSort: useCallback((value: SegmentSort | undefined) => setSort({ value }), []),
    hidden: hiding,
    setHidden: useCallback((value: ReadonlySet<string>) => setHidden({ value }), []),
    rules: filtering,
    asked,
    keyed,
    setRules: useCallback((value: readonly KeyedSegmentRule[]) => setRules({ value }), []),
    ...selection,
    notice,
    problem,
    busy,
    exportShown,
    removeMany,
    editMany,
    edit,
    exportMembers,
    retry: reload,
  }
}
